import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { SOURCE_TCAPI } from '@rsos/constants/locationSources';
import { useFlag } from '@rsos/flags';
import { PreconfiguredMessageInterface } from '@rsos/reduxDataStore/src/features/alerts/chat.types';
import {
  useGetConversation,
  useHasConversationBeenInitiated,
} from '@rsos/reduxDataStore/src/features/conversations/utils';
import { useSelectedCall } from '@rsos/reduxDataStore/src/features/emergencyCalls';
import { StateWithEmergencyCalls } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import { RootState } from '../../../../app';
import isTextBasedSource from '../../../../utils/isSourceSMS';
import AllMessages from '../../../PreconfiguredMessages/AllMessages';
import { PRECONFIG_REQUEST_LOCATION } from '../../../PreconfiguredMessages/AllMessages/utils';
import { useChatContext } from '../ChatContext';
import ConversationsChat from './ConversationsChat';
import ConversationsChatHeader from './ConversationsChatHeader';
import ConversationsChatNext from './ConversationsChatNext';
import {
  AbsoluteAllMessagesContainer,
  ConversationsContainer,
} from './ConversationsChatbox.styles';
import TransferPendingBanner from './TransferToAgency/TransferPendingBanner';

const ConversationsChatBox = () => {
  const callerID = useSelector(
    (state: StateWithEmergencyCalls) => state.emergencyCalls.selectedCallerID,
  );

  const selectedCall = useSelectedCall();
  const mostRecentLocation = getMostRecentLoc(selectedCall);

  const hasRequestLocationAccess = useFlag('rsp_request_location');
  const hasTalkJSImprovements = useFlag('talkjs_improvements');

  const hasRequestLocation = Boolean(
    hasRequestLocationAccess && isTextBasedSource(mostRecentLocation?.source),
  );

  const mapAccessToDynamicMessage: Record<string, boolean> = {
    [PRECONFIG_REQUEST_LOCATION]: hasRequestLocation,
  };

  const preconfiguredMessages = useSelector(
    (state: RootState) => state.preconfiguredMessages?.messages,
  );
  const enabledPreconfiguredMessages = preconfiguredMessages?.filter(
    message => {
      const accessCheck = mapAccessToDynamicMessage[message.title];

      if (accessCheck === undefined) {
        return message.enabled;
      }

      return accessCheck && message.enabled;
    },
  );

  const isConversationStarted = useHasConversationBeenInitiated(callerID);
  const conversation = useGetConversation(callerID);
  const [isAllMessagesVisible, setIsAllMessagesVisible] = useState(false);
  const [selectedPreconfiguredMessage, setSelectedPreconfiguredMessage] =
    useState<PreconfiguredMessageInterface | null>(null);

  const { collapse, isCollapsed } = useChatContext();

  const handleViewClose = () => {
    setIsAllMessagesVisible(false);
  };

  const handleCopyToChat = (selectedMessage: PreconfiguredMessageInterface) => {
    setIsAllMessagesVisible(false);
    setSelectedPreconfiguredMessage(selectedMessage);
  };

  useEffect(() => {
    if (
      conversation?.source === SOURCE_TCAPI &&
      conversation?.data?.acceptStatus === 'timed_out' &&
      !isCollapsed
    ) {
      collapse?.(true);
    }
  }, [collapse, conversation, isCollapsed]);

  return (
    <>
      <TransferPendingBanner conversation={conversation} />
      <ConversationsContainer
        data-name="conversations-container"
        flex={isCollapsed ? '0' : '1'}
      >
        <ConversationsChatHeader
          isConversationStarted={isConversationStarted}
        />
        {isConversationStarted &&
          (hasTalkJSImprovements ? (
            <ConversationsChatNext
              setIsAllMessagesVisible={setIsAllMessagesVisible}
              selectedPreconfiguredMessage={selectedPreconfiguredMessage}
              setSelectedPreconfiguredMessage={setSelectedPreconfiguredMessage}
              hasRequestLocationAccess={hasRequestLocation}
              preconfiguredMessages={enabledPreconfiguredMessages}
            />
          ) : (
            <ConversationsChat
              setIsAllMessagesVisible={setIsAllMessagesVisible}
              selectedPreconfiguredMessage={selectedPreconfiguredMessage}
              setSelectedPreconfiguredMessage={setSelectedPreconfiguredMessage}
              hasRequestLocationAccess={hasRequestLocation}
              preconfiguredMessages={enabledPreconfiguredMessages}
            />
          ))}
        {isAllMessagesVisible && (
          <AbsoluteAllMessagesContainer>
            <AllMessages
              preconfiguredMessages={enabledPreconfiguredMessages}
              onClose={handleViewClose}
              onCopyToChat={handleCopyToChat}
            />
          </AbsoluteAllMessagesContainer>
        )}
      </ConversationsContainer>
    </>
  );
};

export default ConversationsChatBox;
